const { Duplex } = require('stream');
const { LOG_WARN, LOG_TRACE } = require('solclient-log');
const { TransportReturnCode } = require('../transport-return-codes');
const { TransportSessionEventCode } = require('../transport-session-event-codes');

/**
 * This is a Duplex Stream encapsulating a Transport.
 * It transparently passes reads, writes, and events until "shunted".
 * After that, nothing gets through.
 *
 * The intermediate "dontEnd" state allows a TLS shutdown to run its course
 * without closing the underlying transport in the end.
 *
 * Intended lifecycle:
 *
 * 1, create underlying transport
 * 2, create ShuntableTransport with underlying transport
 * 3, pass shuntableTransport to tls.connect
 * 4, connect underlying transport
 *
 * <traffic may flow until TLS shutdown desired>
 *
 * 1, shuntableTransport.dontend();
 * 2, tlsSocket.end();
 * 3, on 'closed' event: take and start using shuntableSocket.socket.
 *
 * For an explanation of _read and _write see
 * https://nodejs.org/api/stream.html#stream_implementing_a_duplex_stream
 *
 * @private
 */

class ShuntableTransport extends Duplex {
  constructor(options) {
    super(options);
    this._shunted = false;
    this._dontEnd = false;
    this._underlyingTransport = options.underlyingTransport;
    // Buffers data-callback pairs until connected or underlying transport decongests.
    // Should never have more than 1 item as long as the stream contract is upheld.
    this._interimBuffer = [];
    this._transportDrained = true;
    this._connected = false;
  }

  shunt() {
    this._shunted = true;
  }

  dontEnd() {
    this._dontEnd = true;
  }

  _write(chunk, encoding, callback) {
    if (!this._shunted) {
      if (!this._transportDrained || !this._connected) {
        this._interimBuffer.push([chunk, callback]);
        return;
      }
      const rc = this._underlyingTransport.send(chunk);
      switch (rc) {
        case TransportReturnCode.OK:
          this._transportDrained = true;
          callback();
          break;
        case TransportReturnCode.NO_SPACE:
          this._transportDrained = false;
          this._interimBuffer.push([chunk, callback]);
          break;
        case TransportReturnCode.INVALID_STATE_FOR_OPERATION:
          this.emit('error',
                    'Underlying transport in invalid state for send.');
          break;
        default:
          LOG_WARN(`Unknown return code from undelying transport ${rc}`);
      }
    }
  }

  //_read(size) {
  _read() {
    if (!this._shunted) {
      this._underlyingTransport.unpause();
    }
  }

  rxDataBuffer(data) {
    if (!this._shunted) {
      const noPause = this.push(data);
      if (!noPause) {
        this._underlyingTransport.pause();
      }
    }
  }

  // underlying transport event dispatch
  eventCB(transportEvent) {
    switch (transportEvent.getTransportEventCode()) {
      case TransportSessionEventCode.UP_NOTICE:
        this.onConnect();
        break;
      case TransportSessionEventCode.SEND_ERROR:
        this.onError(transportEvent);
        break;
      case TransportSessionEventCode.DESTROYED_NOTICE:
        this.onClose(transportEvent);
        break;
      case TransportSessionEventCode.CAN_ACCEPT_DATA:
        this.onDrain();
        break;
      default:
        LOG_WARN(`TLS layer got unkown event code ${transportEvent.getTransportEventCode}`);
    }
  }
  onError(transportEvent) {
    if (!this._shunted) {
      this.emit('error', transportEvent.getInfoStr());
    }
  }

  onClose(transportEvent) {
    this._connected = false;
    if (!this._shunted) {
      LOG_TRACE(`ShuntableTransport emits close for transport event ${transportEvent}`);
      this.emit('close');
    }
  }

  onDrain() {
    if (!this._shunted) {
      this._transportDrained = true;
      if (this._interimBuffer && this._interimBuffer.length > 0) {
        const resendBuffer = this._interimBuffer;
        this._interimBuffer = [];
        resendBuffer.forEach(([data, callback]) => this._write(data, null, callback));
      }
      if (!this._transportDrained) {
        return;
      }
      this.emit('drain');
    }
  }

  onConnect() {
    this._connected = true;
    if (!this._shunted) {
      this.onDrain(); // flush interimbuffer.
      this.emit('connect');
    }
  }

  end(chunk, encoding, callback) {
    if (!this._shunted && !this._dontEnd) {
      if (typeof chunk === 'function') { // sometimes TLS calls end() wrong...
        this._underlyingTransport.destroy('End from TLS');
        chunk();
      } else {
        const cb = () => { this._underlyingTransport.destroy('End from TLS'); callback(); };
        this._write(chunk, encoding, cb);
      }
    } else if (!this._shunted) {
      if (typeof chunk !== 'function') { // sometimes TLS calls end() wrong...
        this._write(chunk, encoding, callback);
      } else {
        chunk();
      }
    }
  }

  destroy(error) {
    if (!this._shunted && !this._dontEnd) {
      this._underlyingTransport.destroy(error);
    }
  }

  /**
   * Force fail the underlying transport's socket to force a failure.
   * Returns transport return code.
   * @param {?String} msg The message associated with this operation, if any.
   * @returns {TransportReturnCode} The result of this operation
   */
  forceFailure(msg) {
    if (!this._shunted && !this._dontEnd && this._underlyingTransport) {
      return this._underlyingTransport.forceFailure(msg);
    }
  }

  setNoDelay(arg) {
    if (!this.shunted) {
      LOG_TRACE(`Ignoring setNoDelay(${arg})`);
    }
  }
}

module.exports.ShuntableTransport = ShuntableTransport;
